import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { buttonStyles } from '@/lib/button-styles'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { SectionHeading } from '@/components/generals/section-heading'
import { cateringProcess } from '@/constants/process'

import {
    ArrowRight,
    Award,
    Briefcase,
    CalendarHeart,
    Gem,
    Globe,
    HeartHandshake,
    House,
    Users
} from 'lucide-react'

const values = [
    {
        icon: Award,
        title: 'Quality',
        text: 'We cook with fresh ingredients, rich traditional seasoning and careful technique, and we never cut corners on portions. Every tray that leaves our kitchen is one we would proudly serve our own family.'
    },
    {
        icon: Gem,
        title: 'Excellence',
        text: 'Presentation matters as much as flavour. From a single surprise tray to a full wedding buffet, we plate, pack and serve with an eye for detail that turns meals into moments.'
    },
    {
        icon: HeartHandshake,
        title: 'Customer Satisfaction',
        text: 'We listen first, plan with you and keep you informed from the first message to the last plate. Your peace of mind is part of the service.'
    }
]

const audiences = [
    {
        icon: House,
        title: 'Individuals and families',
        text: 'Birthdays, Sunday lunches, naming ceremonies and every gathering in between.'
    },
    {
        icon: Briefcase,
        title: 'Businesses',
        text: 'Working lunches, conferences, launches and staff celebrations served on schedule.'
    },
    {
        icon: CalendarHeart,
        title: 'Event planners',
        text: 'A dependable kitchen partner for weddings, receptions and large celebrations.'
    },
    {
        icon: Globe,
        title: 'Nigerians abroad',
        text: 'Meals, trays and packages for loved ones at home, ordered from anywhere in the world.'
    }
]

const About: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Our story'
                description='Meet Loveth Tasty Global, a catering and food services brand in Akure, Nigeria, creating exceptional Nigerian and international food experiences for events, homes and families abroad.'
            />

            <PageHero
                eyebrow='Our Story'
                breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Our Story' }]}
                title={(
                    <>
                        From our kitchen in Akure,{' '}
                        <em className='text-brand-burnt'>to tables around the world.</em>
                    </>
                )}
                description={(
                    <p>
                        Loveth Tasty Global is a growing catering and food services brand built on a simple belief: good food brings people together, and every celebration deserves to taste unforgettable.
                    </p>
                )}
                image={{
                    src: '/images/about/chef-kitchen.jpg',
                    alt: 'A Nigerian chef plating smoky jollof rice, grilled chicken and fried plantain',
                    className: 'object-[50%_35%]'
                }}
            >
                <Link href='/book' className={buttonStyles({ variant: 'primary', size: 'lg' })}>
                    Book Your Catering
                    <ArrowRight className='size-4 transition-transform duration-300 group-hover/button:translate-x-1' aria-hidden='true' />
                </Link>

                <Link href='/#menu' className={buttonStyles({ variant: 'outline', size: 'lg' })}>
                    Explore Our Menu
                </Link>
            </PageHero>

            <section aria-labelledby='who-we-are-title' className='bg-brand-white py-24 lg:py-28'>
                <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-6 lg:grid-cols-12 lg:px-10'>
                    <div className='lg:col-span-5'>
                        <SectionHeading
                            eyebrow='Who We Are'
                            titleId='who-we-are-title'
                            title={(
                                <>
                                    Good Food. Beautiful Moments.{' '}
                                    <em className='text-brand-burnt'>Unforgettable Memories.</em>
                                </>
                            )}
                        />
                    </div>

                    <div className='reveal space-y-6 text-[1.05rem] leading-relaxed text-brand-stone lg:col-span-7'>
                        <p>
                            Loveth Tasty Global is a growing catering and food services brand based in Akure, Ondo State, Nigeria. We are passionate about creating exceptional culinary experiences through delicious local Nigerian meals and international delicacies.
                        </p>

                        <p>
                            Whether you are planning a wedding, celebrating a birthday, organizing a corporate event, or sending a special food surprise to your loved ones in Nigeria from overseas, we make every experience memorable.
                        </p>

                        <p>
                            Our menu moves easily between the smoky party jollof, rich soups and spicy grills that Nigerians grow up loving, and the continental dishes that many modern celebrations call for. Whatever you choose, it is cooked fresh, seasoned with care and presented with the elegance your guests deserve.
                        </p>
                    </div>
                </div>
            </section>

            <section aria-labelledby='mission-title' className='relative overflow-clip bg-brand-charcoal py-24 text-brand-white lg:py-28'>
                <div aria-hidden='true' className='pointer-events-none absolute -top-40 -left-40 size-[30rem] rounded-full bg-brand-orange/15 blur-3xl' />

                <div className='relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-6 lg:grid-cols-12 lg:px-10'>
                    <div className='reveal relative mx-auto w-full max-w-md lg:col-span-5 lg:max-w-none'>
                        <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] rounded-br-[8rem] bg-brand-smoke'>
                            <Image
                                src='/images/hero/nigerian-feast.jpg'
                                alt='Smoky party jollof rice served with grilled chicken, fried plantain and moi moi'
                                fill
                                sizes='(min-width: 1024px) 38vw, (min-width: 640px) 28rem, 100vw'
                                className='object-cover object-[50%_70%]'
                            />
                        </div>
                    </div>

                    <div className='lg:col-span-7'>
                        <p className='reveal inline-flex items-center gap-3 text-[0.72rem] font-semibold tracking-[0.3em] text-brand-gold uppercase'>
                            <span aria-hidden='true' className='h-px w-8 bg-brand-gold/70' />
                            Our Mission
                        </p>

                        <h2 id='mission-title' className='reveal mt-6 font-heading text-[2.2rem] leading-[1.15] font-semibold sm:text-5xl'>
                            To deliver quality, exceptional taste, beautiful presentation, and{' '}
                            <em className='text-brand-orange'>reliable catering services wherever our customers are.</em>
                        </h2>

                        <p className='reveal mt-8 max-w-xl text-brand-mist'>
                            That mission shapes everything we do. We confirm every detail before we cook, and we treat a single food tray with the same care as a full wedding reception.
                        </p>
                    </div>
                </div>
            </section>

            <section aria-labelledby='values-title' className='bg-brand-cream py-24 lg:py-28'>
                <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-10'>
                    <SectionHeading
                        eyebrow='What We Stand For'
                        titleId='values-title'
                        align='center'
                        title={(
                            <>
                                Three promises on{' '}
                                <em className='text-brand-burnt'>every plate</em>
                            </>
                        )}
                    />

                    <ol className='mt-14 grid gap-6 md:grid-cols-3'>
                        {values.map((value, index) => (
                            <li key={value.title} className='reveal relative overflow-hidden rounded-[1.75rem] border border-brand-sand bg-brand-white p-8'>
                                <span aria-hidden='true' className='absolute -top-4 -right-2 font-heading text-[7rem] leading-none font-semibold text-brand-cream'>
                                    {index + 1}
                                </span>

                                <span className='relative inline-flex size-12 items-center justify-center rounded-full bg-brand-orange/15 text-brand-burnt'>
                                    <value.icon className='size-6' aria-hidden='true' />
                                </span>

                                <h3 className='relative mt-6 font-heading text-3xl font-semibold text-brand-charcoal'>
                                    {value.title}
                                </h3>

                                <p className='relative mt-3 leading-relaxed text-brand-stone'>
                                    {value.text}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section aria-labelledby='audience-title' className='bg-brand-white py-24 lg:py-28'>
                <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-6 lg:grid-cols-12 lg:px-10'>
                    <div className='lg:col-span-4'>
                        <SectionHeading
                            eyebrow='Who We Serve'
                            titleId='audience-title'
                            title={(
                                <>
                                    Cooking for{' '}
                                    <em className='text-brand-burnt'>every kind of table</em>
                                </>
                            )}
                            description='Individuals, families, businesses, event planners and Nigerians in the diaspora all trust us with the moments that matter.'
                        />
                    </div>

                    <ul className='grid gap-5 sm:grid-cols-2 lg:col-span-8'>
                        {audiences.map(audience => (
                            <li key={audience.title} className='reveal flex gap-5 rounded-[1.5rem] border border-brand-sand p-6 transition-colors duration-300 hover:border-brand-gold/60 hover:bg-brand-cream/60'>
                                <span className='inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-charcoal text-brand-gold'>
                                    <audience.icon className='size-5' aria-hidden='true' />
                                </span>

                                <div>
                                    <h3 className='text-lg font-semibold text-brand-charcoal'>
                                        {audience.title}
                                    </h3>

                                    <p className='mt-1.5 text-[15px] leading-relaxed text-brand-stone'>
                                        {audience.text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section aria-labelledby='process-title' className='bg-brand-cream py-24 lg:py-28'>
                <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-10'>
                    <SectionHeading
                        eyebrow='How We Work'
                        titleId='process-title'
                        title={(
                            <>
                                Simple from the first message{' '}
                                <em className='text-brand-burnt'>to the last plate</em>
                            </>
                        )}
                    />

                    <ol className='mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
                        {cateringProcess.map((step, index) => (
                            <li key={step.title} className='reveal relative rounded-[1.5rem] bg-brand-white p-7'>
                                <span aria-hidden='true' className='font-heading text-5xl leading-none font-semibold text-brand-gold'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <h3 className='mt-5 text-lg font-semibold text-brand-charcoal'>
                                    {step.title}
                                </h3>

                                <p className='mt-2 text-[15px] leading-relaxed text-brand-stone'>
                                    {step.text}
                                </p>
                            </li>
                        ))}
                    </ol>

                    <div className='reveal mt-14 flex flex-col gap-3 sm:flex-row'>
                        <Link href='/book' className={buttonStyles({ variant: 'primary', size: 'lg' })}>
                            Start Planning Your Event
                            <ArrowRight className='size-4 transition-transform duration-300 group-hover/button:translate-x-1' aria-hidden='true' />
                        </Link>

                        <Link href='/order-from-abroad' className={buttonStyles({ variant: 'dark', size: 'lg' })}>
                            <Users className='size-4' aria-hidden='true' />
                            Order For Family In Nigeria
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default About
